import React, { useEffect, useState } from "react";
import API from "../../api/axiosConfig";
import {
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  TrendingUp, ShoppingBag, Users, Star,
  Clock, Tag, BarChart2, Repeat,
} from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/es";

const PERIOD_OPTIONS = [
  { label: "7 días",  value: "7"   },
  { label: "30 días", value: "30"  },
  { label: "90 días", value: "90"  },
  { label: "Todo",    value: "all" },
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STATUS_LABELS = {
  pendiente:  "Pendiente",
  confirmado: "Confirmado",
  asignado:   "Asignado",
  en_camino:  "En camino",
  entregado:  "Entregado",
  cancelado:  "Cancelado",
};

const STATUS_COLORS = {
  pendiente:  "#f59e0b",
  confirmado: "#3b82f6",
  asignado:   "#8b5cf6",
  en_camino:  "#f97316",
  entregado:  "#10b981",
  cancelado:  "#ef4444",
};

const PIE_COLORS = ["#10b981","#3b82f6","#8b5cf6","#f59e0b","#f97316","#ef4444"];

function fmt(n) {
  return Number(n ?? 0).toLocaleString("es-CO");
}
function fmtCOP(n) {
  return `$${fmt(n)}`;
}
function fmtDate(iso) {
  return dayjs(iso).locale("es").format("DD MMM");
}

function KpiCard({ icon: Icon, label, value, sub, color = "supernova" }) {
  const colorMap = {
    supernova: "bg-supernova-50 text-supernova-600",
    green:     "bg-green-50 text-green-600",
    blue:      "bg-blue-50 text-blue-600",
    purple:    "bg-purple-50 text-purple-600",
    orange:    "bg-orange-50 text-orange-600",
    red:       "bg-red-50 text-red-600",
  };
  return (
    <div className="bg-white rounded-2xl shadow p-5 flex items-start gap-4">
      <div className={`p-3 rounded-xl flex-shrink-0 ${colorMap[color]}`}>
        <Icon size={22} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-800 leading-tight mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="text-base font-bold text-gray-700 mb-3">{children}</h3>;
}

export default function CompanyMetrics() {
  const [data, setData]       = useState(null);
  const [period, setPeriod]   = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const user      = JSON.parse(localStorage.getItem("user") || "null");
  const companyId = user?.company?.id ?? user?.companyId ?? null;

  useEffect(() => {
    if (!companyId || !UUID_RE.test(companyId)) {
      setError(`No se encontró el ID de la empresa (${companyId ?? "null"}).`);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    API.get(`/metrics/company/${companyId}?days=${period}`)
      .then((res) => setData(res.data?.data ?? {}))
      .catch((err) => setError(err.response?.data?.message ?? "Error cargando métricas"))
      .finally(() => setLoading(false));
  }, [companyId, period]);

  if (loading) {
    return (
      <div className="flex justify-center mt-20">
        <div className="animate-spin w-10 h-10 border-4 border-supernova-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto mt-20 text-center text-red-500 bg-red-50 rounded-2xl p-8">
        <p className="font-semibold">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const s  = data.orderStats ?? {};
  const vs = data.summary    ?? {};

  const dailySeries   = (s.dailySeries   ?? []).map(d => ({ ...d, fecha: fmtDate(d.date) }));
  const visitSeries   = (s.visitSeries   ?? []).map(d => ({ ...d, fecha: fmtDate(d.date) }));
  const byStatus      = (s.byStatus      ?? []).map(d => ({ ...d, label: STATUS_LABELS[d.status] ?? d.status }));
  const byHour        = (s.byHour        ?? []).filter(d => d.count > 0);
  const byPayment     = (s.byPayment     ?? []);
  const topSold       = (s.topProductsSold ?? []);
  const topCustomers  = (s.topCustomers  ?? []);
  const topViewed     = (vs.topProducts  ?? []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">

      {/* Header + period selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Métricas y análisis</h2>
          <p className="text-sm text-gray-500 mt-1">Panel de rendimiento de tu tienda</p>
        </div>
        <div className="flex gap-2">
          {PERIOD_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => setPeriod(o.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                period === o.value
                  ? "bg-supernova-500 text-white shadow"
                  : "bg-white text-gray-600 border hover:bg-gray-50"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI cards ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={TrendingUp} color="green"
          label="Ingresos totales"
          value={fmtCOP(s.totalRevenue)}
          sub={`Ticket prom. ${fmtCOP(s.avgTicket)}`}
        />
        <KpiCard
          icon={ShoppingBag} color="blue"
          label="Pedidos recibidos"
          value={fmt(s.totalOrders)}
          sub={`${fmt(s.deliveredOrders)} entregados · ${fmt(s.cancelledOrders)} cancelados`}
        />
        <KpiCard
          icon={Users} color="purple"
          label="Clientes únicos"
          value={fmt(s.uniqueCustomers)}
          sub={`${fmt(s.repeatCustomers)} clientes recurrentes`}
        />
        <KpiCard
          icon={Star} color="supernova"
          label="Calificación promedio"
          value={s.avgRating ? `${s.avgRating} / 5` : "Sin datos"}
          sub={`Tasa de éxito: ${s.conversionRate ?? 0}%`}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Clock} color="orange"
          label="Tiempo promedio entrega"
          value={s.avgDeliveryMinutes ? `${s.avgDeliveryMinutes} min` : "Sin datos"}
          sub="Desde confirmado hasta entregado"
        />
        <KpiCard
          icon={Tag} color="red"
          label="Total descuentos"
          value={fmtCOP(s.totalDiscounts)}
          sub={`${fmt(s.ordersWithCoupon)} pedidos con cupón`}
        />
        <KpiCard
          icon={BarChart2} color="blue"
          label="Visitas al perfil"
          value={fmt(vs.totalVisits)}
          sub={`${fmt(vs.uniqueVisitors)} visitantes únicos`}
        />
        <KpiCard
          icon={Repeat} color="green"
          label="Tasa de conversión"
          value={`${s.conversionRate ?? 0}%`}
          sub="Pedidos entregados / no cancelados"
        />
      </div>

      {/* ── Pedidos + Ingresos por día ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Pedidos por día</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dailySeries} barSize={8}>
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v) => [v, "Pedidos"]} />
              <Bar dataKey="orders" fill="#3b82f6" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Ingresos por día</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={dailySeries}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => [fmtCOP(v), "Ingresos"]} />
              <Area dataKey="revenue" stroke="#10b981" fill="url(#revenueGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Distribuciones ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Estado de pedidos */}
        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Pedidos por estado</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={byStatus}
                dataKey="count"
                nameKey="label"
                cx="50%" cy="50%"
                outerRadius={70}
                innerRadius={35}
                paddingAngle={3}
              >
                {byStatus.map((entry) => (
                  <Cell key={entry.status} fill={STATUS_COLORS[entry.status] ?? "#94a3b8"} />
                ))}
              </Pie>
              <Tooltip formatter={(v, name) => [v, name]} />
              <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Método de pago */}
        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Método de pago</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={byPayment}
                dataKey="count"
                nameKey="method"
                cx="50%" cy="50%"
                outerRadius={70}
                innerRadius={35}
                paddingAngle={3}
              >
                {byPayment.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v, name) => [v, name]} />
              <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Visitas al perfil */}
        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Visitas al perfil</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={visitSeries}>
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line dataKey="visits" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Visitas" />
              <Line dataKey="uniqueVisitors" stroke="#f59e0b" strokeWidth={2} dot={false} name="Únicos" />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Pedidos por hora ──────────────────────────────── */}
      {byHour.length > 0 && (
        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Pedidos por hora del día</SectionTitle>
          <p className="text-xs text-gray-400 mb-3">Identifica tus horas pico para preparar mejor tu equipo</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={byHour} barSize={14}>
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(h) => `${h}h`} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip labelFormatter={(h) => `${h}:00 h`} formatter={(v) => [v, "Pedidos"]} />
              <Bar dataKey="count" fill="#f59e0b" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Top productos ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Productos más vendidos</SectionTitle>
          {topSold.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Sin datos en este período</p>
          ) : (
            <div className="space-y-3">
              {topSold.map((p, i) => (
                <div key={p.id || i} className="flex items-center gap-3">
                  <span className="w-5 text-xs font-bold text-gray-400">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{p.title}</p>
                    <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${Math.round((p.qty / topSold[0].qty) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-800">{fmt(p.qty)} uds.</p>
                    <p className="text-xs text-gray-400">{fmtCOP(p.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Productos más vistos</SectionTitle>
          {topViewed.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Sin datos en este período</p>
          ) : (
            <div className="space-y-3">
              {topViewed.map((p, i) => (
                <div key={p.id || i} className="flex items-center gap-3">
                  <span className="w-5 text-xs font-bold text-gray-400">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{p.title}</p>
                    <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{ width: `${Math.round((p.views / topViewed[0].views) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-800">{fmt(p.views)} vistas</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Top clientes ──────────────────────────────────── */}
      {topCustomers.length > 0 && (
        <div className="bg-white rounded-2xl shadow p-5">
          <SectionTitle>Clientes más frecuentes</SectionTitle>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-gray-500 uppercase">
                  <th className="text-left pb-2 font-medium">#</th>
                  <th className="text-left pb-2 font-medium">Cliente</th>
                  <th className="text-right pb-2 font-medium">Pedidos</th>
                  <th className="text-right pb-2 font-medium">Gasto total</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((c, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2.5 text-gray-400 font-bold">{i + 1}</td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-supernova-100 text-supernova-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {c.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-700">{c.name}</span>
                      </div>
                    </td>
                    <td className="py-2.5 text-right font-semibold text-gray-800">{c.orders}</td>
                    <td className="py-2.5 text-right font-semibold text-green-600">{fmtCOP(c.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
